import { ComboboxGroupData } from "apps/events/types/ComboboxData";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEventTags } from "shared/components/organisms/EventCreationAndEditing/EventCreateEditContext/useEventTags";

export const useWidgetSetupEvents = () => {
  const { t } = useTranslation("widgets", {
    keyPrefix: "CREATE_NEW.EVENT_WIDGET",
  });
  const { organizationId } = useParams();

  /**
   * Look ahead months options
   * - Always from 1 to 12 months
   */
  const lookAheadMonthsOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: (i + 1).toString(),
      label: `${i + 1} ${t(`LOOK_AHEAD.MONTH`, { count: i + 1 })}`,
    }));
  }, [t]);

  /**
   * Load the event tags (all Global Event tags) + all organization tags
   * - The globalEventTags is an array of ComboboxGroupData
   * - The organizationEventTags is an array of ComboboxData
   * - We need to combine these two arrays into a single array of ComboboxGroupData
   */
  const {
    globalEventTags,
    organizationEventTags,
    loading: loadingEventTags,
    error: errorEventTags,
    globalEventTagsLoaded,
    organizationEventTagsLoaded,
  } = useEventTags({
    isOrganization: true,
    organizationId: organizationId || "",
  });
  const [eventTagsLoaded, setEventTagsLoaded] = useState(false);
  const [allEventTags, setAllEventTags] = useState<ComboboxGroupData[]>([]);
  useEffect(() => {
    if (globalEventTagsLoaded && organizationEventTagsLoaded) {
      setAllEventTags([
        ...globalEventTags,
        ...[{ group: "ORGANIZATION_TAGS", tags: organizationEventTags }],
      ]);
      setEventTagsLoaded(true);
    }
  }, [
    globalEventTags,
    organizationEventTags,
    globalEventTagsLoaded,
    organizationEventTagsLoaded,
  ]);

  /**
   * Filter the tags based on the search input
   * - Start with all tags
   */
  const [filteredTags, setFilteredTags] = useState<ComboboxGroupData[]>([]);
  useEffect(() => {
    setFilteredTags([...allEventTags]);
  }, [allEventTags]);

  /**
   * Filter the tags based on the search input
   */
  const handleFilterTags = useCallback(
    (search: string) => {
      const inputValue = (search.toLowerCase().split(",").pop() ?? "").trim();
      const filteredTags = allEventTags
        .map((group) => ({
          ...group,
          tags: group.tags.filter((tag) =>
            tag.label.toLowerCase().includes(inputValue),
          ),
        }))
        .filter((group) => group.tags.length > 0);

      setFilteredTags(filteredTags.length > 0 ? filteredTags : allEventTags);
    },
    [allEventTags],
  );

  /**
   * When the combobox is opened, show all tags
   */
  const handleComboboxOpenChange = useCallback(() => {
    setFilteredTags([...allEventTags]);
  }, [allEventTags]);

  return {
    lookAheadMonthsOptions,
    eventTagsLoaded,
    filteredTags,
    loadingEventTags,
    errorEventTags,
    handleFilterTags,
    handleComboboxOpenChange,
  };
};
